/**
 * Timestamp formatting.
 *
 * The backend stores and serves all timestamps as UTC ISO-8601. For DISPLAY we
 * render in the viewer's local timezone. If the browser timezone cannot be
 * detected, we fall back to Pacific Time (America/Los_Angeles).
 */

const FALLBACK_TZ = 'America/Los_Angeles'

// Resolve the display timezone once: the browser's IANA zone, or Pacific.
export const displayTimeZone: string = (() => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return tz || FALLBACK_TZ
  } catch {
    return FALLBACK_TZ
  }
})()

// Short label for the active zone (e.g. "PDT"), best-effort.
export const displayTzAbbrev: string = (() => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value || displayTimeZone
  } catch {
    return displayTimeZone
  }
})()

/**
 * A timestamp in any of the forms the backend and the browser hand us.
 *
 * The log viewer is the reason this is not just `string`: CloudWatch and ECS
 * events carry epoch MILLISECONDS, and `lastFetch` is a live `Date`. Before this
 * they were formatted inline with `.toISOString()` and `.slice()`, which produced
 * unmarked UTC on a page whose header advertises the viewer's own zone.
 */
export type TimestampLike = string | number | Date | null | undefined

function toDate(ts?: TimestampLike): Date | null {
  if (ts === null || ts === undefined || ts === '') return null
  const d = ts instanceof Date ? ts : new Date(ts)
  return isNaN(d.getTime()) ? null : d
}

/** Full date + time in the display timezone, with zone abbreviation. */
export function fmtDateTime(ts?: TimestampLike): string {
  const d = toDate(ts)
  if (!d) return '—'
  return d.toLocaleString('en-US', {
    timeZone: displayTimeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  })
}

/** Date only, in the display timezone. */
export function fmtDate(ts?: TimestampLike): string {
  const d = toDate(ts)
  if (!d) return '—'
  return d.toLocaleDateString('en-US', { timeZone: displayTimeZone })
}

/** Time only (with seconds and zone abbreviation), in the display timezone. */
export function fmtTime(ts?: TimestampLike): string {
  const d = toDate(ts)
  if (!d) return '—'
  return d.toLocaleTimeString('en-US', {
    timeZone: displayTimeZone,
    timeZoneName: 'short',
  })
}
